<template>
  <div>
    <Navigation />
    <div class="wrap">
      <div class="head">
        <div>
          <h1>Discount Codes</h1>
          <p class="lede">Reusable promo codes for the till and waiter POS.</p>
        </div>
        <button class="primary" @click="openNew" v-if="canEdit">+ New code</button>
      </div>

      <div class="kpi-strip">
        <div class="kpi">
          <div class="kpi-label">Active codes</div>
          <div class="kpi-val">{{ counts.active }}</div>
        </div>
        <div class="kpi teal">
          <div class="kpi-label">Total uses</div>
          <div class="kpi-val">{{ counts.uses }}</div>
        </div>
        <div class="kpi" v-if="counts.expired > 0">
          <div class="kpi-label">Expired</div>
          <div class="kpi-val">{{ counts.expired }}</div>
        </div>
      </div>

      <div class="card">
        <table v-if="codes.length">
          <thead>
            <tr>
              <th>Code</th><th>Description</th><th class="r">Discount</th><th class="r">Min purchase</th>
              <th class="r">Uses</th><th>Valid</th><th>State</th><th></th>
            </tr>
          </thead>
          <tbody>
            <tr v-for="c in codes" :key="c.id" :class="{ inactive: c.state !== 'active' }">
              <td><code class="code-tag">{{ c.code }}</code></td>
              <td class="muted small">{{ c.description || '—' }}</td>
              <td class="r"><strong>{{ formatDiscount(c) }}</strong></td>
              <td class="r muted">{{ c.min_purchase > 0 ? 'KES ' + fmt(c.min_purchase) : '—' }}</td>
              <td class="r">{{ c.uses_count }}{{ c.max_uses ? '/' + c.max_uses : '' }}</td>
              <td class="muted small">
                {{ c.valid_from || '—' }} → {{ c.valid_to || '—' }}
              </td>
              <td><span :class="'state-pill state-' + c.state">{{ c.state }}</span></td>
              <td>
                <button class="secondary small" @click="openEdit(c)" v-if="canEdit">Edit</button>
                <button class="secondary small" @click="toggle(c)" v-if="canEdit" style="margin-left:4px">
                  {{ c.is_active ? 'Disable' : 'Enable' }}
                </button>
              </td>
            </tr>
          </tbody>
        </table>
        <p v-else class="muted center">No discount codes yet. Create one to run promotions at the till.</p>
      </div>
    </div>

    <div v-if="showForm" class="modal-back" @click.self="showForm=false">
      <div class="modal card">
        <h3>{{ form.id ? 'Edit code' : 'New discount code' }}</h3>
        <label>Code</label>
        <input v-model="form.code" @input="form.code = form.code.toUpperCase()" placeholder="e.g. WELCOME10" :disabled="!!form.id" />
        <p class="muted small">Letters, digits, hyphen, underscore. 2–40 chars.</p>
        <label>Description</label>
        <input v-model="form.description" placeholder="e.g. 10% off first-time customers" />
        <div class="row-2">
          <div>
            <label>Discount type</label>
            <select v-model="form.discountType">
              <option value="percent">% off</option>
              <option value="amount">KES amount off</option>
            </select>
          </div>
          <div>
            <label>{{ form.discountType === 'percent' ? 'Percent (0–100)' : 'Amount (KES)' }}</label>
            <input type="number" v-model.number="form.value" min="0" :max="form.discountType === 'percent' ? 100 : undefined" step="0.01" />
          </div>
        </div>
        <div class="row-2">
          <div>
            <label>Min purchase (KES, optional)</label>
            <input type="number" v-model.number="form.minPurchase" min="0" step="0.01" />
          </div>
          <div v-if="form.discountType === 'percent'">
            <label>Cap discount at (KES, optional)</label>
            <input type="number" v-model.number="form.maxDiscount" min="0" step="0.01" />
          </div>
        </div>
        <div class="row-2">
          <div><label>Valid from (optional)</label><input type="date" v-model="form.validFrom" /></div>
          <div><label>Valid to (optional)</label><input type="date" v-model="form.validTo" /></div>
        </div>
        <label>Max uses (optional, blank = unlimited)</label>
        <input type="number" v-model.number="form.maxUses" min="1" />
        <div class="modal-actions">
          <button v-if="form.id && userRole === 'owner'" class="danger" @click="remove" style="margin-right:auto">Delete</button>
          <button class="secondary" @click="showForm=false">Cancel</button>
          <button class="primary" @click="save" :disabled="saving">{{ saving ? 'Saving…' : 'Save' }}</button>
        </div>
        <p class="err" v-if="err">{{ err }}</p>
      </div>
    </div>
  </div>
</template>

<script>
import Navigation from '@/components/paysol-nav/Navigation.vue';
import { paysol, execute } from '@/api';

export default {
  name: 'DiscountCodes',
  components: { Navigation },
  data() {
    return {
      codes: [], userRole: sessionStorage.getItem('userRole') || '',
      showForm: false, saving: false, err: '',
      form: { id: 0, code: '', description: '', discountType: 'percent', value: 10, minPurchase: 0, maxDiscount: 0, validFrom: '', validTo: '', maxUses: 0 },
    };
  },
  computed: {
    canEdit() { return ['owner','manager'].includes(this.userRole); },
    counts() {
      return {
        active: this.codes.filter(c => c.state === 'active').length,
        expired: this.codes.filter(c => c.state === 'expired' || c.state === 'exhausted').length,
        uses: this.codes.reduce((s, c) => s + Number(c.uses_count || 0), 0),
      };
    }
  },
  async mounted() { await this.load(); },
  methods: {
    fmt(n) { return Number(n || 0).toLocaleString('en-KE', { minimumFractionDigits: 2, maximumFractionDigits: 2 }); },
    formatDiscount(c) { return c.discount_type === 'percent' ? Number(c.value) + '% off' : ('KES ' + this.fmt(c.value) + ' off'); },
    async load() {
      const { data } = await execute({ function: 'listDiscountCodes' }, paysol);
      if (data.success) this.codes = data.data;
    },
    openNew() {
      this.form = { id: 0, code: '', description: '', discountType: 'percent', value: 10, minPurchase: 0, maxDiscount: 0, validFrom: '', validTo: '', maxUses: 0 };
      this.err = ''; this.showForm = true;
    },
    openEdit(c) {
      this.form = {
        id: c.id, code: c.code, description: c.description || '',
        discountType: c.discount_type, value: Number(c.value),
        minPurchase: Number(c.min_purchase || 0), maxDiscount: Number(c.max_discount || 0),
        validFrom: c.valid_from || '', validTo: c.valid_to || '', maxUses: Number(c.max_uses || 0),
      };
      this.err = ''; this.showForm = true;
    },
    async save() {
      this.saving = true; this.err = '';
      try {
        const { data } = await execute({ function: 'saveDiscountCode', ...this.form }, paysol);
        if (!data.success) { this.err = data.message; return; }
        this.showForm = false;
        await this.load();
      } finally { this.saving = false; }
    },
    async toggle(c) {
      const { data } = await execute({ function: 'toggleDiscountCode', id: c.id }, paysol);
      if (data.success) await this.load();
    },
    async remove() {
      if (!confirm(`Delete code ${this.form.code}?`)) return;
      const { data } = await execute({ function: 'deleteDiscountCode', id: this.form.id }, paysol);
      if (data.success) { this.showForm = false; await this.load(); alert(data.message); }
      else alert(data.message);
    },
  }
};
</script>

<style scoped>
.wrap { max-width: 1200px; margin: 0 auto; padding: 24px; }
.head { display: flex; justify-content: space-between; align-items: end; margin-bottom: 20px; }
h1 { font-size: 26px; margin: 0 0 4px; color: var(--paysol-text); }
.lede { color: var(--paysol-muted); margin: 0; }
.kpi-strip { display: grid; grid-template-columns: repeat(auto-fit, minmax(180px, 1fr)); gap: 12px; margin-bottom: 12px; }
.kpi { padding: 14px 16px; background: var(--paysol-card); border: 1px solid var(--paysol-line); border-left: 3px solid var(--paysol-muted); border-radius: 6px; }
.kpi.teal { border-left-color: var(--paysol-teal); }
.kpi-label { color: var(--paysol-muted); font-size: 11px; text-transform: uppercase; letter-spacing: 0.5px; font-weight: 600; }
.kpi-val { color: var(--paysol-text); font-size: 22px; font-weight: 700; margin-top: 3px; }
.r { text-align: right; }
.muted { color: var(--paysol-muted); }
.small { font-size: 11px; }
.center { text-align: center; padding: 40px 0; }
.inactive td { opacity: 0.55; }
.err { color: var(--paysol-danger); font-size: 12px; margin-top: 8px; }
button.small { padding: 4px 10px; font-size: 11px; }
.code-tag { background: var(--paysol-teal-glow); color: var(--paysol-teal); padding: 3px 8px; border-radius: 4px; font-family: 'JetBrains Mono', monospace; font-weight: 700; font-size: 12px; }
.state-pill { display: inline-block; padding: 3px 8px; border-radius: 3px; font-size: 10px; font-weight: 700; text-transform: uppercase; letter-spacing: 0.5px; }
.state-active    { background: rgba(16,185,129,.15); color: var(--paysol-success); }
.state-inactive  { background: rgba(139,164,196,.15); color: var(--paysol-muted); }
.state-expired   { background: rgba(239,68,68,.15); color: var(--paysol-danger); }
.state-exhausted { background: rgba(245,158,11,.15); color: var(--paysol-warning); }
.modal-back { position: fixed; inset: 0; background: rgba(0,0,0,0.6); display: flex; align-items: center; justify-content: center; z-index: 50; padding: 24px; }
.modal { width: 100%; max-width: 520px; max-height: 90vh; overflow-y: auto; }
.row-2 { display: grid; grid-template-columns: 1fr 1fr; gap: 12px; }
.modal-actions { display: flex; justify-content: flex-end; gap: 8px; margin-top: 20px; padding-top: 14px; border-top: 1px solid var(--paysol-line); }
</style>
